/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useMemo, useCallback } from "react";
import { Progress, Box, Text, VStack, HStack, Badge, Button, useToast } from "@chakra-ui/react";
import { CheckCircleIcon, CloseIcon } from "@chakra-ui/icons";
import { DataTable } from "../../components/DataTable";
import { getColumnsAbonelik } from "../../components/DataTable/columnsAbonelik";
import { sortTableData } from "../../utils/sort";
import AbonelikContextMenu from "../../components/ContextMenu/AbonelikContextMenu";
import { useUserRole } from "hooks/useUserRole";
import { useColumnOrder } from "hooks/useColumnOrder";
import { useFiltersRef } from "hooks/useFiltersRef";
import ImeiGecmisiModal from "../../components/Modals/Araclar/ImeiGecmisiModal";
import AbonelikYenileModal from "../../components/Modals/Abonelik/AbonelikYenileModal";
import TopluAbonelikYenileModal from "components/Modals/Abonelik/TopluAbonelikYenileModal";
import AbonelikGecmisiModal from "components/Modals/Araclar/AbonelikGecmisiModal";
import AbonelikDetayModal from "components/Modals/Abonelik/AbonelikDetayModal";
import HatirlatmaGonderModal from "components/Modals/Abonelik/HatirlatmaGonderModal";
import AbonelikSonlandirModal from "components/Modals/Abonelik/AbonelikSonlandirModal";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { useFirmOptions } from "hooks/useFirmOptions";
import { useBayiOptions } from "hooks/useBayiOptions";
import FirmSelectorWithFilters from "components/FirmSelectorWithFilters/FirmSelectorWithFilters";
import { FiRefreshCw, FiDownload } from "react-icons/fi";
import { formatFirmaName } from "utils/formatFirmaName";

export default function AbonelikYonetimi() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const [abonelikler, setAbonelikler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedFirmId, setSelectedFirmId] = useState("");
  const userRole = useUserRole();
  const {
    firms,
    loading: firmsLoading,
    error: firmsError,
  } = useFirmOptions();
  // Bayiler hook'u sadece admin ve teknik_eleman için aktif (yetkili bayi göremez)
  const shouldLoadBayiler = userRole === "admin" || userRole === "teknik_eleman";
  const {
    bayiler,
    loading: bayilerLoading,
  } = useBayiOptions(shouldLoadBayiler);
  const selectedFirm = useMemo(
    () =>
      selectedFirmId && selectedFirmId !== "all"
        ? firms.find((firma) => String(firma.id) === String(selectedFirmId))
        : null,
    [firms, selectedFirmId]
  );

  // Uygulanan firma ID'si (Listele butonuna basıldığında güncellenir)
  const [appliedSelectedFirmId, setAppliedSelectedFirmId] = useState("");
  
  const appliedSelectedFirm = useMemo(
    () =>
      appliedSelectedFirmId && appliedSelectedFirmId !== "all"
        ? firms.find((firma) => String(firma.id) === String(appliedSelectedFirmId))
        : null,
    [firms, appliedSelectedFirmId]
  );

  // Filtre state (kullanıcının yazdığı/seçtiği değerler)
  const [plakaFilter, setPlakaFilter] = useState("");
  const [imeiFilter, setImeiFilter] = useState("");
  const [simNoFilter, setSimNoFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [satisTuruFilter, setSatisTuruFilter] = useState("");
  const [bayiFilter, setBayiFilter] = useState("");
  const [uetdsFilter, setUetdsFilter] = useState("");
  
  // Uygulanan filtreler (Listele butonuna basınca bu değerler kullanılacak)
  const [appliedPlakaFilter, setAppliedPlakaFilter] = useState("");
  const [appliedImeiFilter, setAppliedImeiFilter] = useState("");
  const [appliedSimNoFilter, setAppliedSimNoFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [appliedSatisTuruFilter, setAppliedSatisTuruFilter] = useState("");
  const [appliedBayiFilter, setAppliedBayiFilter] = useState("");
  const [appliedUetdsFilter, setAppliedUetdsFilter] = useState("");
  
  // Arama yapılıp yapılmadığını takip et
  const [hasSearched, setHasSearched] = useState(false);

  // Filtreleri ref ile tut - closure sorununu çözmek için
  const filtersRef = useFiltersRef({ plakaFilter, imeiFilter, simNoFilter, statusFilter, satisTuruFilter, bayiFilter, uetdsFilter, selectedFirmId });

  // Role göre kolonları filtrele
  const columns = useMemo(() => {
    return getColumnsAbonelik(userRole);
  }, [userRole]);
  const [columnOrder, setColumnOrder] = useColumnOrder(columns);

  // Sağ tık menü state
  const [context, setContext] = useState({ position: null, row: null });

  // Modal states
  const [imeiGecmisiModal, setImeiGecmisiModal] = useState({
    isOpen: false,
    aracData: null
  });

  const [yenileModal, setYenileModal] = useState({
    isOpen: false,
    abonelik: null,
  });

  const [topluModal, setTopluModal] = useState({
    isOpen: false,
    firmaId: null,
    preselectDeviceId: null,
  });

  const [sonlandirModal, setSonlandirModal] = useState({
    isOpen: false,
    abonelik: null,
  });

  const [abonelikGecmisiModal, setAbonelikGecmisiModal] = useState({
    isOpen: false,
    arac: null,
  });

  const [abonelikDetayModal, setAbonelikDetayModal] = useState({
    isOpen: false,
    abonelik: null,
  });

  const [hatirlatmaModal, setHatirlatmaModal] = useState({
    isOpen: false,
    abonelik: null,
  });

  // API'den abonelikleri çek
  const loadAbonelikler = useCallback((applyFilters = false, firmIdOverride = null, bayiFilterOverride = null) => {
    // Eğer filtreler uygulanacaksa (Listele butonuna basınca)
    if (applyFilters) {
      // Ref'ten güncel filtreleri al
      const currentFilters = filtersRef.current;
      
      // Firma seçimi özel durumu: "Hepsi" seçildiğinde (value="all") bu geçerli bir filtre olarak kabul edilir
      // Böylece tüm firmaları listeleyebiliriz
      const firmaSecildi = currentFilters.selectedFirmId && currentFilters.selectedFirmId !== "";
      
      // En az bir filtre seçilmiş olmalı (firma seçimi "Hepsi" olsa bile geçerli)
      const hasAnyFilter = 
        firmaSecildi || // Firma seçimi yapıldıysa (Hepsi dahil) geçerli
        (currentFilters.plakaFilter && currentFilters.plakaFilter.trim()) ||
        (currentFilters.imeiFilter && currentFilters.imeiFilter.trim()) ||
        (currentFilters.simNoFilter && currentFilters.simNoFilter.trim()) ||
        (currentFilters.statusFilter && currentFilters.statusFilter.trim()) ||
        (currentFilters.satisTuruFilter && currentFilters.satisTuruFilter.trim()) ||
        (currentFilters.bayiFilter && currentFilters.bayiFilter.trim()) ||
        (currentFilters.uetdsFilter && String(currentFilters.uetdsFilter).trim());
      
      if (!hasAnyFilter) {
        toast({
          title: "Uyarı",
          description: "Lütfen en az bir filtre seçin.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setAppliedSelectedFirmId(filtersRef.current.selectedFirmId); // Firma ID'sini de uygula
      setAppliedPlakaFilter(currentFilters.plakaFilter);
      setAppliedImeiFilter(currentFilters.imeiFilter);
      setAppliedSimNoFilter(currentFilters.simNoFilter);
      setAppliedStatusFilter(currentFilters.statusFilter);
      setAppliedSatisTuruFilter(currentFilters.satisTuruFilter);
      setAppliedBayiFilter(currentFilters.bayiFilter);
      setAppliedUetdsFilter(currentFilters.uetdsFilter);
      setHasSearched(true);
    }
    // applyFilters false ise, mevcut uygulanan filtreleri kullanarak veri çek (yenile butonu için)
    
    setLoading(true);
    
    // Firma ID'sini belirle: override varsa onu kullan, yoksa ref'ten selectedFirmId'yi kullan
    // applyFilters false ise (yenile butonu), appliedSelectedFirmId kullan, yoksa ref'ten selectedFirmId kullan
    const firmIdToUse = firmIdOverride !== null 
      ? firmIdOverride 
      : (applyFilters 
          ? filtersRef.current.selectedFirmId 
          : (appliedSelectedFirmId || filtersRef.current.selectedFirmId));
    
    // Bayi filtresi için query parametresi oluştur
    // Yenile butonunda mevcut uygulanan filtreleri kullan, Listele butonunda yeni filtreleri kullan
    const bayiFilterToUse = bayiFilterOverride !== null 
      ? bayiFilterOverride 
      : (applyFilters 
          ? filtersRef.current.bayiFilter 
          : appliedBayiFilter);
    
    const params = new URLSearchParams();
    if (bayiFilterToUse && (userRole === "admin" || userRole === "teknik_eleman")) {
      // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
      if (bayiFilterToUse === "ixpilot") {
        params.append("bayi_id", "null");
      } else {
        params.append("bayi_id", bayiFilterToUse);
      }
    }

    const rowFilters = applyFilters
      ? {
          plaka: filtersRef.current.plakaFilter,
          imei: filtersRef.current.imeiFilter,
          simNo: filtersRef.current.simNoFilter,
          status: filtersRef.current.statusFilter,
          satisTuru: filtersRef.current.satisTuruFilter,
          uetds: filtersRef.current.uetdsFilter,
        }
      : {
          plaka: appliedPlakaFilter,
          imei: appliedImeiFilter,
          simNo: appliedSimNoFilter,
          status: appliedStatusFilter,
          satisTuru: appliedSatisTuruFilter,
          uetds: appliedUetdsFilter,
        };
    if (rowFilters.plaka?.trim()) params.append("plaka", rowFilters.plaka.trim());
    if (rowFilters.imei?.trim()) params.append("imei", rowFilters.imei.trim());
    if (rowFilters.simNo?.trim()) params.append("sim_no", rowFilters.simNo.trim());
    if (rowFilters.status?.trim()) params.append("status", rowFilters.status.trim());
    if (rowFilters.satisTuru?.trim()) params.append("satis_turu", rowFilters.satisTuru.trim());
    const uetdsRow = typeof rowFilters.uetds === "string" ? rowFilters.uetds : "";
    if (uetdsRow === "acik") params.append("uetds", "true");
    if (uetdsRow === "kapali") params.append("uetds", "false");
    
    // Firma seçiliyse o firmaya ait abonelikleri getir
    // Firma ID'sini query parametresi olarak ekle (opsiyonel)
    if (firmIdToUse && firmIdToUse !== "all") {
      params.append("firma_id", firmIdToUse);
    }
    
    // Her zaman aynı endpoint'i kullan, firma_id query parametresi ile filtreleme yapılır
    const url = `/api/abonelikler/${params.toString() ? `?${params.toString()}` : ""}`;
    
    api
      .get(url)
      .then((res) => res.json())
      .then((data) => {
        setAbonelikler(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        // Merkezi error handling - Güvenli mesaj göster
        handleError(error, "Abonelik verileri yükleme hatası");
        setAbonelikler([]);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userRole,
    appliedBayiFilter,
    appliedSelectedFirmId,
    appliedPlakaFilter,
    appliedImeiFilter,
    appliedSimNoFilter,
    appliedStatusFilter,
    appliedSatisTuruFilter,
    appliedUetdsFilter,
  ]);


  // Progress gösterme komponenti
  const ProgressDisplay = ({ kalanGun, progressYuzdesi }) => {
    if (!kalanGun || kalanGun <= 0) {
      return (
        <Badge colorScheme="red" variant="subtle">
          Süresi Dolmuş
        </Badge>
      );
    }

    // Backend'den gelen progress_yuzdesi: geçen sürenin yüzdesi (0-100)
    // Frontend'de gösterilmesi gereken: kalan sürenin yüzdesi (100 - geçen)
    let kalanYuzde = 100;
    
    if (progressYuzdesi !== null && progressYuzdesi !== undefined && !isNaN(progressYuzdesi)) {
      // Backend'den gelen progress_yuzdesi geçen sürenin yüzdesi
      // Kalan sürenin yüzdesi = 100 - geçen sürenin yüzdesi
      kalanYuzde = Math.round(100 - progressYuzdesi);
      kalanYuzde = Math.max(0, Math.min(100, kalanYuzde));
    } else {
      // Backend'den progress_yuzdesi gelmediyse, kalan günü kullanarak tahmin yap
      // Bu durum sadece fallback olarak kullanılmalı
      return (
        <VStack spacing={1}>
          <Text fontSize="xs" color="gray.600">
            {kalanGun} gün
          </Text>
          <Text fontSize="xs" color="gray.400">
            Progress hesaplanamadı
          </Text>
        </VStack>
      );
    }
    
    // Renk belirleme - Yüzde bazlı gradyanlı geçiş (yeşil → sarı → turuncu → kırmızı)
    const getColorScheme = () => {
      if (kalanYuzde >= 75) return "green";     // %75-100 → yeşil (çok iyi)
      if (kalanYuzde >= 50) return "yellow";    // %50-74 → sarı (orta)
      if (kalanYuzde >= 25) return "orange";    // %25-49 → turuncu (dikkat)
      return "red";                             // %0-24 → kırmızı (kritik)
    };

    // Kritik durum için yanıp sönen animasyon (%25'in altında)
    const isBlinking = kalanYuzde < 25 && kalanYuzde > 0;    return (
      <VStack spacing={1} align="stretch" minW="80px">
        <Progress 
          value={kalanYuzde}
          colorScheme={getColorScheme()}
          size="sm"
          borderRadius="md"
          bg="gray.200"
          sx={{
            // Gradyanlı progress bar - Yüzde bazlı renklendirme
            "& > div": {
              background: kalanYuzde >= 75 
                ? "linear-gradient(90deg, #38a169 0%, #68d391 100%)"  // Yeşil gradyan (%75-100)
                : kalanYuzde >= 50 
                ? "linear-gradient(90deg, #d69e2e 0%, #f6e05e 100%)"  // Sarı gradyan (%50-74)  
                : kalanYuzde >= 25
                ? "linear-gradient(90deg, #dd6b20 0%, #f6ad55 100%)"  // Turuncu gradyan (%25-49)
                : "linear-gradient(90deg, #e53e3e 0%, #fc8181 100%)", // Kırmızı gradyan (%0-24)
              // Kritik durum için yanıp sönen animasyon (%25'in altında)
              ...(kalanYuzde < 25 && kalanYuzde > 0 && {
                animation: "blink 1.5s ease-in-out infinite",
                "@keyframes blink": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 }
                }
              })
            }
          }}
        />
        <HStack justify="space-between" spacing={1}>
          <Text 
            fontSize="xs" 
            color={isBlinking ? "red.600" : "gray.600"} 
            fontWeight={isBlinking ? "bold" : "medium"}
            sx={{
              // Metin de yanıp sönsün
              ...(isBlinking && {
                animation: "blink 1.5s ease-in-out infinite",
                "@keyframes blink": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.4 }
                }
              })
            }}
          >
            {kalanGun} gün
          </Text>
          <Text fontSize="xs" color="gray.500">
            %{kalanYuzde}
          </Text>
        </HStack>
      </VStack>
    );
  };

  // Filtreler backend'de; burada sadece tablo görünümü.
  const filteredRows = useMemo(() => {
    return abonelikler.map((r) => {
      const normalizeFirma = (val) =>
        String(val || "")
          .toLowerCase()
          .replace(/ı/g, "i"); // türkçe 'ı' -> 'i'
      const isSold = normalizeFirma(r.firma).includes("satilan");

      // Satış türü görünümünü formatla
      const formatSatisTuru = (value) => {
        if (!value || value === "-") return "-";
        if (value === "Satılık") return "Satış";
        if (value === "Kiralık") return "Kiralama";
        return value; // Test veya diğer değerler aynen kalır
      };

      return {
        ...r,
        is_sold: isSold,
        firma: isSold ? "satilan" : formatFirmaName(r.firma || ""),
        satis_turu: formatSatisTuru(r.satis_turu), // Formatlanmış görünüm
        satis_turu_raw: r.satis_turu, // Raw değer (Satılık/Kiralık) - backend için
        dongu_durumu_raw: r.dongu_durumu,
        // ham tarih değerlerini modal vb. için sakla
        dongu_tarihi_raw: r.dongu_tarihi,
        yenilendigi_tarih_raw: r.yenilendigi_tarih,
        satis_tarihi_raw: r.satis_tarihi,
        dongu_durumu: isSold ? (
          <Text color="purple.500" fontWeight="semibold">Satılan araç</Text>
        ) : r.dongu_durumu === "aktif" ? (
          <CheckCircleIcon color="green.400" boxSize={5} />
        ) : r.dongu_durumu === "pasif" && r.manuel_sonlandirma_aciklama ? (
          <VStack spacing={0} align="start">
            <Text color="orange.500" fontWeight="semibold" fontSize="xs">
              Manuel Sonlandırıldı
            </Text>
            <Text color="gray.500" fontSize="xs" noOfLines={1} maxW="200px">
              {r.manuel_sonlandirma_aciklama}
            </Text>
          </VStack>
        ) : (
          <CloseIcon color="red.400" boxSize={4} />
        ),
        kalan_abonelik: isSold ? (
          <Text color="gray.500">-</Text>
        ) : (
          <ProgressDisplay 
            kalanGun={r.kalan_gun} 
            progressYuzdesi={r.progress_yuzdesi} 
          />
        ),
        yenilendigi_tarih: isSold
          ? "-"
          : r.yenilendigi_tarih 
            ? new Date(r.yenilendigi_tarih).toLocaleDateString("tr-TR")
            : "-",
        dongu_tarihi: isSold
          ? "-"
          : r.dongu_tarihi
            ? new Date(r.dongu_tarihi).toLocaleDateString("tr-TR")
            : "-",
        satis_tarihi: r.satis_tarihi
          ? new Date(r.satis_tarihi).toLocaleDateString("tr-TR")
          : "-",
        uetds: isSold ? (
          <Text color="gray.500">-</Text>
        ) : r.uetds === true ? (
          <CheckCircleIcon color="green.500" boxSize={5} />
        ) : r.uetds === false ? (
          <CloseIcon color="red.500" boxSize={4} />
        ) : (
          <Text fontSize="xs" color="gray.500">
            Veri yok
          </Text>
        ),
      };
    });
  }, [abonelikler]);

  // Sıralama
  const sortedData = useMemo(
    () => sortTableData(filteredRows, sortColumn, sortOrder),
    [filteredRows, sortColumn, sortOrder]
  );

  const handleSort = (col) => {
    if (sortColumn === col) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSortColumn(col);
      setSortOrder("asc");
    }
  };

  // Sağ tık handler
  const handleContextMenu = (e, row) => {
    e.preventDefault();
    setContext({ position: { x: e.clientX, y: e.clientY }, row });
  };

  const closeMenu = () => setContext({ position: null, row: null });

  // IMEI Geçmişi Modal Handler
  const handleImeiGecmisi = (aracData) => {
    setImeiGecmisiModal({
      isOpen: true,
      aracData: aracData
    });
    closeMenu();
  };

  const closeImeiGecmisiModal = () => {
    setImeiGecmisiModal({
      isOpen: false,
      aracData: null
    });
  };

  const handleOpenYenile = (row) => {
    // row: DataTable satır verisi (filteredRows'tan geliyor)
    setYenileModal({
      isOpen: true,
      abonelik: row,
    });
  };

  const closeYenileModal = () => {
    setYenileModal({
      isOpen: false,
      abonelik: null,
    });
  };

  const handleOpenTopluYenile = (row) => {
    if (!row) return;

    let targetFirmaId = selectedFirmId;

    // Eğer "Hepsi" görünümündeysek, tıklanan satırın firmasından ID'yi bul
    if (!targetFirmaId || targetFirmaId === "all") {
      const matched = firms.find(
        (f) => String(f.name || "").toLowerCase() === String(row.firma || "").toLowerCase()
      );
      if (matched) {
        targetFirmaId = matched.id;
      }
    }

    // Hâlâ firma bulunamadıysa uyar
    if (!targetFirmaId || targetFirmaId === "all") {
      toast({
        title: "Firma bulunamadı",
        description: "Satırdaki firmayı bulamadık, lütfen firmayı seçin.",
        status: "warning",
      });
      return;
    }

    setTopluModal({
      isOpen: true,
      firmaId: targetFirmaId,
      preselectDeviceId: row?.cihaz_id || null,
    });
  };

  const closeTopluModal = () => {
    setTopluModal({
      isOpen: false,
      firmaId: null,
      preselectDeviceId: null,
    });
  };

  const handleOpenAbonelikGecmisi = (row) => {
    if (!row) return;
    setAbonelikGecmisiModal({
      isOpen: true,
      arac: {
        id: row.cihaz_id,
        plaka: row.plaka,
        firma: row.firma,
        imei: row.imei,
      },
    });
  };

  const closeAbonelikGecmisiModal = () => {
    setAbonelikGecmisiModal({
      isOpen: false,
      arac: null,
    });
  };

  const handleOpenAbonelikDetay = (row) => {
    if (!row) return;
    setAbonelikDetayModal({
      isOpen: true,
      abonelik: row,
    });
  };

  const handleOpenSonlandir = (row) => {
    if (!row) return;
    setSonlandirModal({
      isOpen: true,
      abonelik: row,
    });
  };

  const handleDownloadPDF = async () => {
    // Listeleme yapılmadan PDF indirilemesin
    if (!hasSearched) {
      toast({
        title: "Uyarı",
        description: "Önce 'Listele' butonuna basarak abonelikleri listeleyin.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Query parametrelerini oluştur
      const params = new URLSearchParams();
      if (appliedSelectedFirmId && appliedSelectedFirmId !== "all") {
        params.append("firma_id", appliedSelectedFirmId);
      }
      // Bayi filtresi (sadece admin ve teknik_eleman için)
      // Önce appliedBayiFilter'ı kontrol et, yoksa filtersRef'ten al
      const currentBayiFilter = appliedBayiFilter || filtersRef.current.bayiFilter;
      if (currentBayiFilter && (userRole === "admin" || userRole === "teknik_eleman")) {
        // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
        if (currentBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", currentBayiFilter);
        }
      }
      if (appliedPlakaFilter?.trim()) params.append("plaka", appliedPlakaFilter.trim());
      if (appliedImeiFilter?.trim()) params.append("imei", appliedImeiFilter.trim());
      if (appliedSimNoFilter?.trim()) params.append("sim_no", appliedSimNoFilter.trim());
      if (appliedStatusFilter?.trim()) params.append("status", appliedStatusFilter.trim());
      if (appliedSatisTuruFilter?.trim()) params.append("satis_turu", appliedSatisTuruFilter.trim());
      if (appliedUetdsFilter === "acik") params.append("uetds", "true");
      if (appliedUetdsFilter === "kapali") params.append("uetds", "false");
      
      const queryString = params.toString();
      const url = `/api/abonelikler/pdf${queryString ? `?${queryString}` : ""}`;
      
      // PDF'i indir - apiFetch kullanarak blob response al
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("PDF oluşturulamadı");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      // Dosya adı
      let filename = "abonelik_raporu.pdf";
      if (appliedSelectedFirm && appliedSelectedFirm.name) {
        filename = `abonelik_raporu_${appliedSelectedFirm.name.replace(/\s+/g, '_')}.pdf`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "PDF indirildi",
        description: "Abonelik raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "PDF indirme hatası");
    }
  };

  const handleDownloadExcel = async () => {
    if (!hasSearched) {
      toast({
        title: "Uyarı",
        description: "Önce 'Listele' butonuna basarak abonelikleri listeleyin.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setExcelLoading(true);
      const params = new URLSearchParams();
      if (appliedSelectedFirmId && appliedSelectedFirmId !== "all") {
        params.append("firma_id", appliedSelectedFirmId);
      }
      const currentBayiFilter = appliedBayiFilter || filtersRef.current.bayiFilter;
      if (currentBayiFilter && (userRole === "admin" || userRole === "teknik_eleman")) {
        if (currentBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", currentBayiFilter);
        }
      }
      if (appliedPlakaFilter?.trim()) params.append("plaka", appliedPlakaFilter.trim());
      if (appliedImeiFilter?.trim()) params.append("imei", appliedImeiFilter.trim());
      if (appliedSimNoFilter?.trim()) params.append("sim_no", appliedSimNoFilter.trim());
      if (appliedStatusFilter?.trim()) params.append("status", appliedStatusFilter.trim());
      if (appliedSatisTuruFilter?.trim()) params.append("satis_turu", appliedSatisTuruFilter.trim());
      if (appliedUetdsFilter === "acik") params.append("uetds", "true");
      if (appliedUetdsFilter === "kapali") params.append("uetds", "false");

      const queryString = params.toString();
      const url = `/api/abonelikler/excel${queryString ? `?${queryString}` : ""}`;
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error("Excel oluşturulamadı");
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      let filename = "abonelik_raporu.xlsx";
      if (appliedSelectedFirm && appliedSelectedFirm.name) {
        filename = `abonelik_raporu_${appliedSelectedFirm.name.replace(/\s+/g, "_")}.xlsx`;
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Excel indirildi",
        description: "Abonelik raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      handleError(error, "Excel indirme hatası");
    } finally {
      setExcelLoading(false);
    }
  };

  const closeSonlandirModal = () => {
    setSonlandirModal({
      isOpen: false,
      abonelik: null,
    });
  };

  const closeAbonelikDetayModal = () => {
    setAbonelikDetayModal({
      isOpen: false,
      abonelik: null,
    });
  };

  const handleOpenHatirlatma = (row) => {
    if (!row) return;
    setHatirlatmaModal({
      isOpen: true,
      abonelik: row,
    });
  };

  const closeHatirlatmaModal = () => {
    setHatirlatmaModal({
      isOpen: false,
      abonelik: null,
    });
  };

  const filterFields = [
    ...(userRole === "admin" || userRole === "teknik_eleman" ? [{
      key: "bayi",
      type: "select",
      label: "Bayi",
      placeholder: "Bayi Seçiniz",
      value: bayiFilter,
      onChange: setBayiFilter,
      options: [
        { label: "Tümü", value: "" },
        { label: "ixPilot", value: "ixpilot" },
        ...bayiler.map((bayi) => ({
          label: bayi.name,
          value: bayi.id,
        })),
      ],
      isDisabled: bayilerLoading,
    }] : []),
    {
      key: "plaka",
      type: "text",
      label: "Plaka",
      placeholder: "Plaka",
      value: plakaFilter,
      onChange: setPlakaFilter,
    },
    {
      key: "imei",
      type: "text",
      label: "IMEI",
      placeholder: "IMEI",
      value: imeiFilter,
      onChange: setImeiFilter,
    },
    {
      key: "sim_no",
      type: "text",
      label: "SIM No",
      placeholder: "SIM No",
      value: simNoFilter,
      onChange: setSimNoFilter,
    },
    {
      key: "status",
      type: "select",
      label: "Abonelik Durumu",
      placeholder: "Abonelik Durumu",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { label: "Aktif", value: "aktif" },
        { label: "Pasif", value: "pasif" },
        { label: "Süresi Dolanlar", value: "expired" },
        { label: "Son 1 Ay Kalan", value: "last30" },
      ],
    },
    {
      key: "satis_turu",
      type: "select",
      label: "Satış Türü",
      placeholder: "Satış Türü",
      value: satisTuruFilter,
      onChange: setSatisTuruFilter,
      options: [
        { label: "Satış", value: "Satılık" },
        { label: "Kiralama", value: "Kiralık" },
      ],
    },
    {
      key: "uetds",
      type: "select",
      label: "UETDS",
      placeholder: "UETDS",
      value: uetdsFilter,
      onChange: setUetdsFilter,
      options: [
        { label: "Tümü", value: "" },
        { label: "Açık", value: "acik" },
        { label: "Kapalı", value: "kapali" },
      ],
    },
  ];

  return (
    <Box position="relative" mt={{ base: 2, md: 6 }}>
      <FirmSelectorWithFilters
        title="Abonelikler"
        firms={firms}
        selectedFirmId={selectedFirmId}
        onChange={setSelectedFirmId}
        loading={firmsLoading}
        error={firmsError}
        includeAllOption
        fields={filterFields}
        onSearch={() => loadAbonelikler(true)}
        searchButtonLoading={loading}
        searchButtonLoadingText="Listeleniyor..."
        onReset={() => {
          setPlakaFilter("");
          setImeiFilter("");
          setSimNoFilter("");
          setStatusFilter("");
          setSatisTuruFilter("");
          setBayiFilter("");
          setUetdsFilter("");
          setAppliedPlakaFilter("");
          setAppliedImeiFilter("");
          setAppliedSimNoFilter("");
          setAppliedStatusFilter("");
          setAppliedSatisTuruFilter("");
          setAppliedBayiFilter("");
          setAppliedUetdsFilter("");
          setAppliedSelectedFirmId("");
          setHasSearched(false);
          // Listeyi temizle
          setAbonelikler([]);
          // filtersRef'i de güncelle
          filtersRef.current = {
            plakaFilter: "",
            imeiFilter: "",
            simNoFilter: "",
            statusFilter: "",
            satisTuruFilter: "",
            bayiFilter: "",
            uetdsFilter: "",
            selectedFirmId: "",
          };
          // Firma seçimini de sıfırla
          setSelectedFirmId("");
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            loadAbonelikler(true);
          }
        }}
      />

      <DataTable
          title={
            appliedSelectedFirm
              ? `Abonelik Yönetimi • ${appliedSelectedFirm.name} • ${sortedData.length} abonelik`
              : `Abonelik Yönetimi • ${sortedData.length} abonelik`
        }
        filterBar={
          <HStack justify="flex-end" spacing={3}>
            <Button
              size="sm"
              leftIcon={<FiDownload />}
              colorScheme="blue"
              variant="outline"
              onClick={handleDownloadPDF}
              isLoading={loading}
              loadingText="Hazırlanıyor"
              isDisabled={!hasSearched}
            >
              PDF İndir
            </Button>
            <Button
              size="sm"
              leftIcon={<FiDownload />}
              colorScheme="green"
              variant="outline"
              onClick={handleDownloadExcel}
              isLoading={excelLoading}
              loadingText="Hazırlanıyor"
              isDisabled={!hasSearched}
            >
              Excel İndir
            </Button>
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              variant="ghost"
              onClick={() => loadAbonelikler(true, appliedSelectedFirmId || filtersRef.current.selectedFirmId, appliedBayiFilter)}
              isLoading={loading}
              loadingText="Yükleniyor"
              isDisabled={!hasSearched}
            >
              Yenile
            </Button>
          </HStack>
        }
        columns={columns}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        data={sortedData}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
        tableFontSize="11px"
        tableRowHeight="40px"
        loading={loading}
        hasSearched={hasSearched}
        emptyMessage={
          hasSearched
            ? "Arama kriterlerinize uygun kayıt bulunamadı."
            : "Henüz liste yüklenmedi. Firma seçin veya filtreleri doldurup 'Listele' butonuna basarak arama yapabilirsiniz."
        }
        onRowContextMenu={handleContextMenu}
      />

      {/* Sağ tık menüsü */}
      <AbonelikContextMenu
        position={context.position}
        data={context.row}
        onClose={closeMenu}
        onImeiGecmisi={handleImeiGecmisi}
        onYenile={handleOpenYenile}
        onOpenGecmis={handleOpenAbonelikGecmisi}
        onOpenDetay={handleOpenAbonelikDetay}
        onOpenHatirlatma={handleOpenHatirlatma}
        onOpenTopluYenile={handleOpenTopluYenile}
        onOpenSonlandir={handleOpenSonlandir}
      />

      {/* IMEI Geçmişi Modal */}
      <ImeiGecmisiModal
        isOpen={imeiGecmisiModal.isOpen}
        onClose={closeImeiGecmisiModal}
        aracData={imeiGecmisiModal.aracData}
      />

      <AbonelikYenileModal
        isOpen={yenileModal.isOpen}
        onClose={closeYenileModal}
        abonelik={yenileModal.abonelik}
        onSuccess={() => {
          loadAbonelikler(true, appliedSelectedFirmId || filtersRef.current.selectedFirmId, appliedBayiFilter);
        }}
      />

      <TopluAbonelikYenileModal
        isOpen={topluModal.isOpen}
        onClose={closeTopluModal}
        firmaId={topluModal.firmaId}
        preselectDeviceId={topluModal.preselectDeviceId}
        onSuccess={() => {
          loadAbonelikler(true, appliedSelectedFirmId || filtersRef.current.selectedFirmId, appliedBayiFilter);
        }}
      />

      <AbonelikGecmisiModal
        isOpen={abonelikGecmisiModal.isOpen}
        onClose={closeAbonelikGecmisiModal}
        aracData={abonelikGecmisiModal.arac}
      />

      <AbonelikDetayModal
        isOpen={abonelikDetayModal.isOpen}
        onClose={closeAbonelikDetayModal}
        abonelik={abonelikDetayModal.abonelik}
      />

      <HatirlatmaGonderModal
        isOpen={hatirlatmaModal.isOpen}
        onClose={closeHatirlatmaModal}
        abonelik={hatirlatmaModal.abonelik}
      />

      <AbonelikSonlandirModal
        isOpen={sonlandirModal.isOpen}
        onClose={closeSonlandirModal}
        abonelik={sonlandirModal.abonelik}
        onSuccess={() => {
          loadAbonelikler(true, appliedSelectedFirmId || filtersRef.current.selectedFirmId, appliedBayiFilter);
        }}
      />
    </Box>
  );
}